import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth, deptFeature } from '../../context/AuthContext';
import { departmentsAPI, projectsAPI } from '../../api/client';
import Modal from '../Common/Modal';
import {
  HomeIcon, UsersIcon, CogIcon, ChevronDownIcon,
  FolderIcon, ShieldCheckIcon, ArchiveBoxIcon, ArrowUturnLeftIcon, TrashIcon,
  CalendarDaysIcon, DocumentChartBarIcon, PresentationChartLineIcon,
  PhoneArrowUpRightIcon, ChatBubbleLeftRightIcon,
  ChevronDoubleLeftIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const getDeptColor = (dept) => {
  const n = (dept.name || '').toLowerCase();
  if (n.includes('hr') || n.includes('human resource')) return '#16a34a';
  if (n.includes('finance')) return '#d97706';
  if (n.includes('account')) return '#2563eb';
  if (n.includes('it') || n.includes('tech')) return '#7c3aed';
  return dept.color || '#4f46e5';
};

// Shared container classes: mobile slide-in drawer + desktop static collapsible.
// `collapsed` only drives lg: variants, so the mobile drawer is never affected.
const asideClass = (collapsed, mobileOpen) => `
  fixed inset-y-0 left-0 z-40 bg-white flex flex-col h-full border-r border-slate-100
  transform transition-all duration-300 ease-in-out w-64
  ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
  lg:static lg:translate-x-0 lg:z-auto lg:flex-shrink-0
  ${collapsed ? 'lg:w-[68px]' : 'lg:w-64'}
`;

export default function Sidebar({ collapsed, onToggleCollapse, onExpand, mobileOpen, onCloseMobile }) {
  const { user, isCallCenter, canDelete, hasFeature } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState({});
  const [expandedDepts, setExpandedDepts] = useState({});
  const [projectsOpen, setProjectsOpen] = useState({});   // per-dept "Projects" group (collapsed by default)
  const [archivedProjects, setArchivedProjects] = useState({}); // per-dept archived list (lazy-loaded)
  const [archivedOpen, setArchivedOpen] = useState({});   // per-dept "Archived" group (collapsed by default)
  const [archivingProject, setArchivingProject] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);   // archived project pending permanent delete
  const [deleteConfirmText, setDeleteConfirmText] = useState(''); // must match project name to enable delete
  const [permaDeleting, setPermaDeleting] = useState(false);

  useEffect(() => {
    // Call-center users have no department visibility at all.
    if (isCallCenter) {
      setDepartments([]);
      return;
    }
    departmentsAPI.list().then((res) => {
      setDepartments(res.data);
      if (user?.department_id) {
        setExpandedDepts({ [user.department_id]: true });
        loadProjects(user.department_id);
      } else if (res.data.length === 1) {
        setExpandedDepts({ [res.data[0].id]: true });
        loadProjects(res.data[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isCallCenter]);

  const loadProjects = async (deptId) => {
    if (projects[deptId]) return;
    const res = await projectsAPI.list(deptId);
    setProjects((prev) => ({ ...prev, [deptId]: res.data }));
  };

  const toggleDept = (deptId) => {
    const expanded = !expandedDepts[deptId];
    setExpandedDepts((prev) => ({ ...prev, [deptId]: expanded }));
    if (expanded) loadProjects(deptId);
  };

  // When collapsed (desktop icon strip), clicking a dept avatar expands the
  // whole sidebar and opens that department.
  const handleDeptClick = (deptId) => {
    if (collapsed) {
      onExpand?.();
      setExpandedDepts((prev) => ({ ...prev, [deptId]: true }));
      loadProjects(deptId);
    } else {
      toggleDept(deptId);
    }
  };

  const toggleProjects = (deptId) => {
    setProjectsOpen((prev) => ({ ...prev, [deptId]: !prev[deptId] }));
    loadProjects(deptId);
  };

  const loadArchived = async (deptId, force = false) => {
    if (archivedProjects[deptId] && !force) return;
    const res = await projectsAPI.listArchived(deptId);
    setArchivedProjects((prev) => ({ ...prev, [deptId]: res.data }));
  };

  const toggleArchived = (deptId) => {
    const willOpen = !archivedOpen[deptId];
    setArchivedOpen((prev) => ({ ...prev, [deptId]: willOpen }));
    if (willOpen) loadArchived(deptId);
  };

  const requestArchive = (e, proj) => {
    e.preventDefault();
    e.stopPropagation();
    setArchivingProject(proj);
  };

  const confirmArchive = async () => {
    if (!archivingProject) return;
    const { id, department_id, name } = archivingProject;
    setArchiving(true);
    try {
      await projectsAPI.archive(id);
      // Remove from the active list and drop the dept badge count.
      setProjects((prev) => ({
        ...prev,
        [department_id]: (prev[department_id] || []).filter((p) => p.id !== id),
      }));
      setDepartments((prev) =>
        prev.map((d) =>
          d.id === department_id
            ? { ...d, project_count: Math.max(0, (d.project_count || 0) - 1) }
            : d
        )
      );
      // If the Archived list for this dept is already loaded, surface it there.
      if (archivedProjects[department_id]) {
        const archivedProj = { ...archivingProject, is_archived: true };
        setArchivedProjects((prev) => ({
          ...prev,
          [department_id]: [archivedProj, ...(prev[department_id] || [])],
        }));
      }
      toast.success(`Project "${name}" archived`);
      setArchivingProject(null);
      if (location.pathname === `/project/${id}`) navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive project');
    } finally {
      setArchiving(false);
    }
  };

  const restoreProject = async (proj) => {
    const { id, department_id } = proj;
    setRestoringId(id);
    try {
      const res = await projectsAPI.restore(id);
      // Remove from archived list; add back to the active list if it's loaded.
      setArchivedProjects((prev) => ({
        ...prev,
        [department_id]: (prev[department_id] || []).filter((p) => p.id !== id),
      }));
      if (projects[department_id]) {
        setProjects((prev) => ({
          ...prev,
          [department_id]: [res.data, ...(prev[department_id] || [])],
        }));
      }
      setDepartments((prev) =>
        prev.map((d) =>
          d.id === department_id
            ? { ...d, project_count: (d.project_count || 0) + 1 }
            : d
        )
      );
      toast.success(`Project "${proj.name}" restored`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore project');
    } finally {
      setRestoringId(null);
    }
  };

  const requestPermanentDelete = (proj) => {
    setDeleteConfirmText('');
    setDeletingProject(proj);
  };

  const confirmPermanentDelete = async () => {
    if (!deletingProject) return;
    const { id, department_id, name } = deletingProject;
    setPermaDeleting(true);
    try {
      await projectsAPI.permanentDelete(id);
      // Drop it from the archived list (archived projects aren't in the active
      // badge count, so no project_count change is needed).
      setArchivedProjects((prev) => ({
        ...prev,
        [department_id]: (prev[department_id] || []).filter((p) => p.id !== id),
      }));
      toast.success(`Project "${name}" permanently deleted`);
      setDeletingProject(null);
      if (location.pathname === `/project/${id}`) navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    } finally {
      setPermaDeleting(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  // Call-center: minimal sidebar with logo + Call Monitor link only.
  if (isCallCenter) {
    return (
      <aside className={asideClass(collapsed, mobileOpen)} style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.04)' }}>
        <SidebarHeader collapsed={collapsed} onToggleCollapse={onToggleCollapse} onCloseMobile={onCloseMobile} />
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <p className={`px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 ${collapsed ? 'lg:hidden' : ''}`}>
            Workspace
          </p>
          <NavItem
            to="/call-monitor"
            icon={PhoneArrowUpRightIcon}
            label="Call Monitor"
            active={isActive('/call-monitor')}
            collapsed={collapsed}
          />
        </nav>
      </aside>
    );
  }

  return (
    <aside className={asideClass(collapsed, mobileOpen)} style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.04)' }}>
      <SidebarHeader collapsed={collapsed} onToggleCollapse={onToggleCollapse} onCloseMobile={onCloseMobile} />

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        <NavItem to="/" icon={HomeIcon} label="Dashboard" active={isActive('/')} collapsed={collapsed} />

        <div className="pt-4">
          <p className={`px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 ${collapsed ? 'lg:hidden' : ''}`}>
            Departments
          </p>
          {departments.length === 0 && (
            <p className={`px-3 py-2 text-xs text-slate-400 italic ${collapsed ? 'lg:hidden' : ''}`}>No departments assigned</p>
          )}
          {departments.map((dept) => {
            // Hide departments whose gating module the user lacks (HR/Finance/Accounts).
            const gate = deptFeature(dept.name);
            if (gate && !hasFeature(gate)) return null;

            const color = getDeptColor(dept);
            const bg = color + '18';
            const isExpanded = expandedDepts[dept.id];
            const userCanDelete = canDelete(dept.id);
            const deptProjects = projects[dept.id] || [];

            return (
              <div key={dept.id}>
                <button
                  onClick={() => handleDeptClick(dept.id)}
                  title={collapsed ? dept.name : undefined}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm hover:bg-slate-50 transition-colors ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
                >
                  <span
                    className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: bg, color }}
                  >
                    {dept.name.charAt(0).toUpperCase()}
                  </span>
                  <span className={`flex-1 text-left font-medium text-slate-700 truncate ${collapsed ? 'lg:hidden' : ''}`}>
                    {dept.name}
                  </span>
                  {dept.project_count > 0 && (
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 ${collapsed ? 'lg:hidden' : ''}`}
                      style={{ backgroundColor: bg, color }}
                    >
                      {dept.project_count}
                    </span>
                  )}
                  <ChevronDownIcon
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 flex-shrink-0 ${collapsed ? 'lg:hidden' : ''} ${
                      isExpanded ? 'rotate-0' : '-rotate-90'
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div
                    className={`animate-slide-down ml-4 pl-2.5 mt-0.5 mb-1 space-y-0.5 ${collapsed ? 'lg:hidden' : ''}`}
                    style={{ borderLeft: `2px solid ${color}30` }}
                  >
                    {/* Collapsible "Projects" group (collapsed by default) */}
                    {hasFeature('ai_projects') && (
                      <>
                        <button
                          onClick={() => toggleProjects(dept.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                        >
                          <FolderIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: color + 'aa' }} />
                          <span className="flex-1 text-left">Projects</span>
                          {dept.project_count > 0 && (
                            <span className="text-[10px] text-slate-400 font-semibold">{dept.project_count}</span>
                          )}
                          <ChevronDownIcon
                            className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                              projectsOpen[dept.id] ? 'rotate-0' : '-rotate-90'
                            }`}
                          />
                        </button>

                        {projectsOpen[dept.id] && (
                          <div className="animate-slide-down ml-3 space-y-0.5">
                            {deptProjects.length === 0 ? (
                              <p className="px-2 py-1 text-[11px] text-slate-400 italic">No projects yet</p>
                            ) : (
                              deptProjects.map((proj) => {
                                const active = isActive(`/project/${proj.id}`);
                                return (
                                  <div
                                    key={proj.id}
                                    className={`group flex items-center rounded-md transition-colors ${
                                      active ? 'bg-primary-50' : 'hover:bg-slate-50'
                                    }`}
                                  >
                                    <Link
                                      to={`/project/${proj.id}`}
                                      className={`flex items-center gap-2 px-2 py-1.5 flex-1 min-w-0 text-xs ${
                                        active ? 'text-primary-700 font-semibold' : 'text-slate-600'
                                      }`}
                                    >
                                      <FolderIcon
                                        className="w-3.5 h-3.5 flex-shrink-0"
                                        style={{ color: active ? undefined : color + 'aa' }}
                                      />
                                      <span className="truncate">{proj.name}</span>
                                    </Link>
                                    {userCanDelete && (
                                      <button
                                        onClick={(e) => requestArchive(e, proj)}
                                        title="Archive project"
                                        aria-label={`Archive project ${proj.name}`}
                                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 mr-1 rounded text-slate-300 hover:text-amber-600 hover:bg-amber-50 transition-opacity"
                                      >
                                        <ArchiveBoxIcon className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}

                        {/* Archived projects group (collapsed by default) */}
                        {userCanDelete && (
                          <>
                            <button
                              onClick={() => toggleArchived(dept.id)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
                            >
                              <ArchiveBoxIcon className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="flex-1 text-left">Archived</span>
                              {(archivedProjects[dept.id]?.length > 0) && (
                                <span className="text-[10px] text-slate-400 font-semibold">
                                  {archivedProjects[dept.id].length}
                                </span>
                              )}
                              <ChevronDownIcon
                                className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                                  archivedOpen[dept.id] ? 'rotate-0' : '-rotate-90'
                                }`}
                              />
                            </button>

                            {archivedOpen[dept.id] && (
                              <div className="animate-slide-down ml-3 space-y-0.5">
                                {!archivedProjects[dept.id] ? (
                                  <p className="px-2 py-1 text-[11px] text-slate-400 italic">Loading…</p>
                                ) : archivedProjects[dept.id].length === 0 ? (
                                  <p className="px-2 py-1 text-[11px] text-slate-400 italic">No archived projects</p>
                                ) : (
                                  archivedProjects[dept.id].map((proj) => (
                                    <div
                                      key={proj.id}
                                      className="group flex items-center rounded-md hover:bg-slate-50 transition-colors"
                                    >
                                      <span className="flex items-center gap-2 px-2 py-1.5 flex-1 min-w-0 text-xs text-slate-400">
                                        <FolderIcon className="w-3.5 h-3.5 flex-shrink-0 text-slate-300" />
                                        <span className="truncate line-through">{proj.name}</span>
                                      </span>
                                      <button
                                        onClick={() => restoreProject(proj)}
                                        disabled={restoringId === proj.id}
                                        title="Restore project"
                                        aria-label={`Restore project ${proj.name}`}
                                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded text-slate-300 hover:text-green-600 hover:bg-green-50 transition-opacity disabled:opacity-50"
                                      >
                                        <ArrowUturnLeftIcon className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => requestPermanentDelete(proj)}
                                        title="Delete permanently"
                                        aria-label={`Permanently delete project ${proj.name}`}
                                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 mr-1 rounded text-slate-300 hover:text-red-600 hover:bg-red-50 transition-opacity"
                                      >
                                        <TrashIcon className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {/* Feature links — direct, always visible when the dept is expanded */}
                    {dept.name === 'Finance' && (
                      <DeptLink
                        to={`/finance-reports/${dept.id}`}
                        icon={PresentationChartLineIcon}
                        label="Financial Reports"
                        active={location.pathname.includes('finance-reports')}
                        activeClass="bg-amber-50 text-amber-700"
                      />
                    )}

                    {dept.name === 'HR' && (
                      <>
                        <DeptLink
                          to={`/cv-screening/${dept.id}`}
                          icon={UsersIcon}
                          label="CV Screening"
                          active={location.pathname.includes('cv-screening')}
                          activeClass="bg-green-50 text-green-700"
                        />
                        <DeptLink
                          to={`/form-builder/${dept.id}`}
                          icon={CogIcon}
                          label="Form Builder"
                          active={location.pathname.includes('form-builder')}
                          activeClass="bg-green-50 text-green-700"
                        />
                        <DeptLink
                          to={`/interviews/${dept.id}`}
                          icon={CalendarDaysIcon}
                          label="Interview Scheduling"
                          active={location.pathname.includes('interviews')}
                          activeClass="bg-green-50 text-green-700"
                        />
                        <DeptLink
                          to={`/reports/${dept.id}`}
                          icon={DocumentChartBarIcon}
                          label="HR Reports"
                          active={location.pathname.includes('/reports/')}
                          activeClass="bg-green-50 text-green-700"
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      <div className="border-t border-slate-100 px-3 py-3 space-y-0.5">
        {hasFeature('calendar') && (
          <NavItem to="/calendar" icon={CalendarDaysIcon} label="Calendar" active={location.pathname.startsWith('/calendar')} collapsed={collapsed} />
        )}
        {hasFeature('teams') && (
          <NavItem to="/teams" icon={ChatBubbleLeftRightIcon} label="Teams Mentions" active={location.pathname.startsWith('/teams')} collapsed={collapsed} />
        )}
        {hasFeature('call_monitor') && (
          <NavItem to="/call-monitor" icon={PhoneArrowUpRightIcon} label="Call Monitor" active={isActive('/call-monitor')} collapsed={collapsed} />
        )}
        {hasFeature('admin_panel') && (
          <NavItem to="/admin" icon={ShieldCheckIcon} label="Admin Panel" active={isActive('/admin')} collapsed={collapsed} />
        )}
      </div>

      <Modal
        open={!!archivingProject}
        onClose={() => !archiving && setArchivingProject(null)}
        title="Archive this project?"
        size="sm"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <ArchiveBoxIcon className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-sm text-slate-600">
            You are about to archive{' '}
            <span className="font-semibold text-slate-900">"{archivingProject?.name}"</span>.
            All data will be preserved and can be restored later.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setArchivingProject(null)}
            disabled={archiving}
            className="px-4 py-2 text-sm rounded-xl text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={confirmArchive}
            disabled={archiving}
            className="px-4 py-2 text-sm rounded-xl text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <ArchiveBoxIcon className="w-4 h-4" />
            {archiving ? 'Archiving…' : 'Archive project'}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!deletingProject}
        onClose={() => !permaDeleting && setDeletingProject(null)}
        title="Permanently delete this project?"
        size="sm"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <TrashIcon className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-red-700">ALL data will be deleted forever</span> —
            candidates, CVs, form responses, chat history. This cannot be undone.
          </p>
        </div>

        <label className="block text-xs font-medium text-slate-600 mb-1.5">
          Type the project name{' '}
          <span className="font-semibold text-slate-900">"{deletingProject?.name}"</span> to confirm:
        </label>
        <input
          type="text"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          disabled={permaDeleting}
          placeholder={deletingProject?.name || ''}
          autoFocus
          className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent disabled:opacity-50"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setDeletingProject(null)}
            disabled={permaDeleting}
            className="px-4 py-2 text-sm rounded-xl text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={confirmPermanentDelete}
            disabled={permaDeleting || deleteConfirmText.trim() !== deletingProject?.name}
            className="px-4 py-2 text-sm rounded-xl text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <TrashIcon className="w-4 h-4" />
            {permaDeleting ? 'Deleting…' : 'Yes, delete permanently'}
          </button>
        </div>
      </Modal>
    </aside>
  );
}

function SidebarHeader({ collapsed, onToggleCollapse, onCloseMobile }) {
  return (
    <div className="px-3 py-4 border-b border-slate-100 flex items-center gap-3">
      <div
        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${collapsed ? 'lg:hidden' : ''}`}
        style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
      >
        <span className="text-white font-extrabold text-xs tracking-tight">UNC</span>
      </div>
      <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
        <p className="font-bold text-slate-900 text-sm leading-none">UNC AI</p>
        <p className="text-[11px] text-slate-400 mt-0.5 leading-none">Workspace</p>
      </div>

      {/* Desktop collapse / expand toggle */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={`hidden lg:flex w-7 h-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex-shrink-0 ${collapsed ? 'lg:mx-auto' : ''}`}
      >
        <ChevronDoubleLeftIcon className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
      </button>

      {/* Mobile close */}
      <button
        onClick={onCloseMobile}
        aria-label="Close menu"
        className="lg:hidden w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex-shrink-0"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, active, collapsed }) {
  return (
    <Link
      to={to}
      title={collapsed ? label : undefined}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all relative ${
        collapsed ? 'lg:justify-center lg:px-0' : ''
      } ${
        active
          ? 'bg-primary-50 text-primary-700 font-semibold'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
      }`}
    >
      {active && (
        <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-600 rounded-r-full ${collapsed ? 'lg:hidden' : ''}`} />
      )}
      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary-600' : 'text-slate-400'}`} />
      <span className={collapsed ? 'lg:hidden' : ''}>{label}</span>
    </Link>
  );
}

function DeptLink({ to, icon: Icon, label, active, activeClass }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors font-medium ${
        active ? activeClass : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
      }`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      {label}
    </Link>
  );
}
